import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import _ from 'lodash';

import {
  Button,
  MaterialIcon,
} from '@pearsontechnology/aero-components';

import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';

import {
  PageActions,
  PageContent,
} from 'components/PageStyles';

import { featureKeys } from 'services/FeatureFlagService';

import {
  ScrollWrapper,
  Table,
  TableHead,
  TableRow,
  TableHeaderCell,
  TableHeaderCellStudentColumn,
  TableHeaderCellScoreColumn,
  TableHeaderCellWalkableColumn,
  Subheading,
  TableHeaderCellSecondLine,
  TableBody,
  TableCell,
  ScrollButton,
  TableHeaderCellScrollButton,
} from '../Grid/TableNew';
import AccountSummaryNew from '../Grid/AccountSummaryNew';
import HeaderCellValue from '../Grid/HeaderCellValue';
import GradesSummary, { GradesSummaryPlaceholder } from '../Grid/GradesSummary';
import CSVDownloadButton from '../CSVDownloadButton';

import messages from './messages';
import { scoreTypes, childWalkables, learners } from './data';
import { computeData } from './computedData';
import useGradesGridNew from './useGradesGridNew';
import enhanceWithFeatureFlagSubscription from 'hocs/enhanceWithFeatureFlagSubscription';

const InlineButton = styled(Button)`
  display: inline-flex;
`;

const PopperContainer = styled.div`
  > div {
    z-index: 40;
  }
`;

function GradesGridNew({ navigationActions, getFeatureFlagValue }) {
  const {
    popperRef,
    scrollWrapperRef,
    scrollLeftButtonEnabled,
    scrollRightButtonEnabled,
    onScroll,
    scrollRight200,
    scrollLeft200,
  } = useGradesGridNew();

  const {
    learnersWithPercentages,
    totalPercentage,
    childWalkablesWithScoreSummaries,
    gradesSummaryShouldBeShown,
  } = computeData(scoreTypes, childWalkables, learners);

  // This should always be true in this file
  const isNewDesignEnabled = getFeatureFlagValue(featureKeys.WORKSPACE_GRADES_NEW_DESIGN);

  return (
    <React.Fragment>
      <PageActions style={{ overflowX: 'auto', paddingTop: 4, flex: isNewDesignEnabled ? 'none' : undefined }}>
        {navigationActions}
        <CSVDownloadButton
          label=""
          disabled
        />
      </PageActions>
      <PageContent flex100={isNewDesignEnabled}>
        <PopperContainer ref={popperRef} />
        <ScrollWrapper
          ref={scrollWrapperRef}
          onScroll={onScroll}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell topLeftHeader>
                  <TableHeaderCellStudentColumn>
                    <FormattedMessage {...messages.studentName} />
                    <Subheading><FormattedMessage {...messages.numberOfStudents} values={{ num: learners.length }} /></Subheading>

                    <TableHeaderCellSecondLine>
                      <AccountSummaryNew allStudents />
                    </TableHeaderCellSecondLine>
                  </TableHeaderCellStudentColumn>

                  <TableHeaderCellScoreColumn>
                    <FormattedMessage {...messages.total} />
                    <Subheading>%</Subheading>

                    <TableHeaderCellSecondLine>
                      <HeaderCellValue>{totalPercentage}%</HeaderCellValue>
                    </TableHeaderCellSecondLine>
                  </TableHeaderCellScoreColumn>
                </TableHeaderCell>

                <TableHeaderCellScrollButton left>
                  <ScrollButton
                    iconOnly
                    grey
                    disabled={!scrollLeftButtonEnabled}
                    onClick={scrollLeft200}
                  >
                    <MaterialIcon id="chevron_left" />
                  </ScrollButton>
                </TableHeaderCellScrollButton>

                {childWalkablesWithScoreSummaries.map((childWalkable) => (
                  <TableHeaderCell
                    key={childWalkable.id}
                    style={{ minWidth: 150 }}
                  >
                    <TableHeaderCellWalkableColumn>
                      <span>{childWalkable.title || intl.formatMessage(messages.noTitle)}</span>
                      {childWalkable.totalScore !== null && childWalkable.totalScore !== undefined ? (
                        <Subheading>{childWalkable.totalScore}pts</Subheading>
                      ) : (
                        <Subheading>&nbsp;</Subheading>
                      )}

                      <TableHeaderCellSecondLine style={{ width: 120, padding: '3px 0' }}>
                        {gradesSummaryShouldBeShown[childWalkable.id].value ? (
                          <GradesSummary
                            lowest={childWalkable.lowest}
                            mean={childWalkable.mean}
                            median={childWalkable.median}
                            highest={childWalkable.highest}
                            totalScore={Number(childWalkable.totalScore)}
                            popperRef={popperRef}
                          />
                        ) : (
                          <GradesSummaryPlaceholder />
                        )}
                      </TableHeaderCellSecondLine>
                    </TableHeaderCellWalkableColumn>
                  </TableHeaderCell>
                ))}

                <TableHeaderCellScrollButton right>
                  <ScrollButton
                    iconOnly
                    grey
                    disabled={!scrollRightButtonEnabled}
                    onClick={scrollRight200}
                  >
                    <MaterialIcon id="chevron_right" />
                  </ScrollButton>
                </TableHeaderCellScrollButton>
              </TableRow>
            </TableHead>
            <TableBody>
              {learnersWithPercentages.map(({ percentage, ...otherFields }) => (
                <TableRow key={otherFields.id}>
                  <TableHeaderCell>
                    <TableHeaderCellStudentColumn>
                      <AccountSummaryNew account={otherFields} />
                    </TableHeaderCellStudentColumn>
                    <TableHeaderCellScoreColumn>
                      <HeaderCellValue>{percentage}%</HeaderCellValue>
                    </TableHeaderCellScoreColumn>
                  </TableHeaderCell>

                  <TableHeaderCellScrollButton left />

                  {childWalkables.map((childWalkable) => {
                    const resultScoreObj = _.find(otherFields.scores, { walkableId: childWalkable.id });
                    if (!resultScoreObj) {
                      return <TableCell key={childWalkable.id} />;
                    }
                    if (resultScoreObj.scoreType === scoreTypes.UNATTEMPTED) {
                      return <TableCell key={childWalkable.id} light><FormattedMessage {...messages.unattempted} /></TableCell>;
                    }
                    if (resultScoreObj.scoreType === scoreTypes.NA) {
                      return <TableCell key={childWalkable.id} light><FormattedMessage {...messages.na} /></TableCell>;
                    }
                    if (resultScoreObj.scoreType === scoreTypes.IN_PROGRESS) {
                      return <TableCell key={childWalkable.id} light><FormattedMessage {...messages.inProgress} /></TableCell>;
                    }
                    if (resultScoreObj.scoreType === scoreTypes.GRADED) {
                      return <TableCell key={childWalkable.id}>{resultScoreObj.score}pts</TableCell>;
                    }
                    if (resultScoreObj.scoreType === scoreTypes.PARTIALLY_GRADED) {
                      return <TableCell key={childWalkable.id}>{resultScoreObj.score}pts <InlineButton flat><FormattedMessage {...messages.gradeButtonLabel} /></InlineButton></TableCell>;
                    }
                    return <TableCell key={childWalkable.id} />;
                  })}

                  <TableHeaderCellScrollButton right />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollWrapper>
      </PageContent>
    </React.Fragment>
  );
}

GradesGridNew.propTypes = {
  navigationActions: PropTypes.node.isRequired,
};

const flagNames = [featureKeys.WORKSPACE_GRADES_NEW_DESIGN];

export default enhanceWithFeatureFlagSubscription(GradesGridNew, flagNames);
